import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { fetchWithInterceptor } from "../../api/client";
import { ROUTE_MAPPINGS } from "../../api/types";

const readHistory = (): string[] => {
  const stored = localStorage.getItem("debug-console-history");
  if (!stored) return [];
  try {
    return JSON.parse(stored);
  } catch (e) {
    console.error("Failed to parse history from local storage", e);
    return [];
  }
};

export const DebugConsole = () => {
  const [path, setPath] = useState("/api/transactions");
  const [history, setHistory] = useState<string[]>(readHistory);
  const [response, setResponse] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const handleCopy = async () => {
    if (response) {
      try {
        await navigator.clipboard.writeText(response);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        setCopyError(
          `Couldn't copy: ${err instanceof Error ? err.message : String(err)}`,
        );
        setTimeout(() => setCopyError(null), 4000);
      }
    }
  };

  const handleSend = async () => {
    setLoading(true);
    setResponse(null);
    setStatus(null);
    setError(null);

    // Update history
    if (path && !history.includes(path)) {
      const newHistory = [path, ...history].slice(0, 50); // Keep last 50
      setHistory(newHistory);
      localStorage.setItem("debug-console-history", JSON.stringify(newHistory));
    }

    try {
      const res = await fetchWithInterceptor(path);
      setStatus(res.status);

      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await res.json();
        setResponse(JSON.stringify(data, null, 2));
      } else {
        const text = await res.text();
        setResponse(text);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const options = Array.from(new Set([...history, ...ROUTE_MAPPINGS]));

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Debug Console
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
          freeSolo
          fullWidth
          options={options}
          value={path}
          onInputChange={(_, newValue) => setPath(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="API Path"
              variant="outlined"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSend();
                }
              }}
            />
          )}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loading}
          sx={{ minWidth: "100px" }}
        >
          {loading ? <CircularProgress size={24} /> : "Send"}
        </Button>
      </Box>

      {copyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {copyError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {status !== null && (
        <Typography variant="subtitle1" gutterBottom>
          Status: {status}
        </Typography>
      )}

      {response && (
        <Box sx={{ position: "relative" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCopy}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            {copied ? "Copied!" : "Copy JSON"}
          </Button>
          <Paper
            sx={{
              p: 2,
              bgcolor: "background.paper",
              overflowX: "auto",
              maxHeight: "60vh",
            }}
          >
            <pre style={{ margin: 0, fontSize: "0.875rem" }}>{response}</pre>
          </Paper>
        </Box>
      )}
    </Box>
  );
};
